import { Product } from "pages/vendor/Products/Products"
import React from "react"
import {
  Card,
  CardBody,
  Col,
  Row,
} from "reactstrap"
import { ProductActionMenu } from "./Components/ProductActionMenu"
import { useTranslation } from "react-i18next"

interface ProductCardProps {
  product: Product
  viewDetailsHandler: (product: Product) => void
  editProductHandler: (product: Product) => void
  deleteProductHandler: (product: Product) => void
  toggleActiveProduct: (product: Product) => void
  addNewOffer: (product: Product) => void
  addNewCampaign: (product: Product) => void
  duplicateProductHandler: (product: Product) => void
}

const ProductCard: React.FC<ProductCardProps> = ({
  product,
  deleteProductHandler,
  editProductHandler,
  viewDetailsHandler,
  toggleActiveProduct,
  addNewCampaign,
  addNewOffer,
  duplicateProductHandler,
}) => {
  const { t, i18n } = useTranslation()
  const isArabic = i18n.language === "ar"

  const primaryImage = product.images?.find(img => img.isPrimary || img.isMain)
  const imageUrl = primaryImage?.filePath
    ? `${process.env.REACT_APP_BACKEND_URL}${primaryImage.filePath}`
    : "https://placehold.co/300x200/png"

  return (
    <Col xxl="3" xl="4" sm="6">
      <Card className="text-center rounded-3 overflow-hidden shadow-md">
        <CardBody className="p-0">
          <div
            className="position-relative overflow-hidden"
            style={{ maxHeight: "200px" }}
          >
            <img
              src={imageUrl}
              className="card-img-top"
              alt={isArabic ? product.arName : product.enName}
              style={{ objectFit: "cover", height: "200px" }}
            />
            {false && (
              <span
                className="badge bg-success text-white fw-semibold position-absolute"
                style={{ top: "10px", right: "10px", fontSize: "0.8rem" }}
              >
                {t("products_featured")}
              </span>
            )}
          </div>

          <div className="card-body p-2 px-3">
            <div className="d-flex justify-content-between align-items-start p-2">
              <div className="d-flex justify-content-between align-items-start w-100">
                <div className="text-start">
                  <h6 className="mb-0 fw-bold">
                    {isArabic ? product.arName : product.enName}
                  </h6>
                  <small className="text-muted">
                    {product.categoryName || "-"}
                  </small>
                </div>
                <ProductActionMenu
                  product={product}
                  viewDetailsHandler={viewDetailsHandler}
                  editProductHandler={editProductHandler}
                  deleteProductHandler={deleteProductHandler}
                  addNewOffer={addNewOffer}
                  addNewCampaign={addNewCampaign}
                  duplicateProductHandler={duplicateProductHandler}
                />
              </div>
            </div>

            <div className="p3 px-2">
              <div className="d-flex justify-content-between mt-3">
                <span className="fw-bold">
                  {t("products_stock")}: {product.totalAvailableQuantity}
                </span>
                <span className="fw-bold">{t("common_size")}: __</span>
              </div>

              <div className="d-flex justify-content-between align-items-center mt-1">
                <span className="fw-bold">QAR {product.price.toFixed(2)}</span>
                <span className="badge bg-primary-30 text-primary fw-semibold me-1 px-2 py-1 font-size-10">
                  __
                </span>
              </div>
            </div>
            <div className="form-check form-switch mt-3">
              <input
                type="checkbox"
                className="form-check-input"
                id={`customSwitch${product.id}`}
                checked={product.isActive}
                onClick={() => toggleActiveProduct(product)}
                onChange={e => {
                  e.stopPropagation()
                }}
              />
              <label
                className="form-check-label text-capitalize"
                htmlFor={`customSwitch${product.id}`}
              >
                {product.isActive ? "Active" : "Inactive"}
              </label>
            </div>
          </div>
        </CardBody>
      </Card>
    </Col>
  )
}

interface ProductsGridProps {
  products: Product[]
  viewDetailsHandler: (product: Product) => void
  editProductHandler: (product: Product) => void
  deleteProductHandler: (product: Product) => void
  toggleActiveProduct: (product: Product) => void
  addNewOffer: (product: Product) => void
  addNewCampaign: (product: Product) => void
  duplicateProductHandler: (product: Product) => void
}

export const ProductsGrid: React.FC<ProductsGridProps> = ({
  products,
  deleteProductHandler,
  editProductHandler,
  viewDetailsHandler,
  toggleActiveProduct,
  addNewCampaign,
  addNewOffer,
  duplicateProductHandler,
}) => {
  return (
    <Row>
      {products.map(product => (
        <ProductCard
          key={product.id}
          product={product}
          editProductHandler={editProductHandler}
          deleteProductHandler={deleteProductHandler}
          viewDetailsHandler={viewDetailsHandler}
          toggleActiveProduct={toggleActiveProduct}
          addNewCampaign={addNewCampaign}
          addNewOffer={addNewOffer}
          duplicateProductHandler={duplicateProductHandler}
        />
      ))}
    </Row>
  )
}
